import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Button,
  TextField,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ShieldIcon from '@mui/icons-material/Shield';
import VerifiedIcon from '@mui/icons-material/Verified';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';

const Profile = () => {
  const auth = useContext(CONTEXTS.AuthContext);
  const api = useContext(CONTEXTS.APIContext);
  const userController = useMemo(() => new CONTROLLERS.UserController(api), [api]);
  const authController = useMemo(() => new CONTROLLERS.AuthController(api), [api]);

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState({
    displayName: '',
    email: '',
    bio: '',
    city: '',
    country: '',
  });

  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);
  const avatarInputRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorDialogOpen, setTwoFactorDialogOpen] = useState(false);
  const [twoFactorOtp, setTwoFactorOtp] = useState('');
  const [twoFactorPendingEnable, setTwoFactorPendingEnable] = useState(false);
  const [twoFactorSubmitting, setTwoFactorSubmitting] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (auth.user) {
      setCurrentUser(auth.user);
      setProfile({
        displayName: auth.user.profile?.displayName || auth.user.username || '',
        email: auth.user.email || '',
        bio: auth.user.profile?.bio || '',
        city: auth.user.profile?.city || '',
        country: auth.user.profile?.country || '',
      });

      setTwoFactorEnabled(Boolean(auth.user?.settings?.twoFactorEnabled));
      setLoading(false);
    }
  }, [auth.user]);

  useEffect(() => {
    if (!selectedAvatarFile) {
      setAvatarPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedAvatarFile);
    setAvatarPreviewUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
    };
  }, [selectedAvatarFile]);

  // Helper functions
  const getDisplayName = () => {
    if (!currentUser) return 'Loading...';
    return currentUser.profile?.displayName || currentUser.username || 'User';
  };

  const getAvatar = () => {
    if (!currentUser) return null;
    return avatarPreviewUrl || currentUser.profile?.currentAvatar?.url || null;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'moderator': return 'primary';
      default: return 'default';
    }
  };

  const getRoleLabel = () => {
    if (!currentUser) return 'User';
    if (currentUser.role === 'admin' && currentUser.adminLevel) {
      const levelLabels = {
        super_admin: 'Super Admin',
        system_admin: 'System Admin',
        content_manager: 'Content Manager',
        support_admin: 'Support Admin',
        finance_admin: 'Finance Admin',
        read_only_admin: 'Read Only Admin'
      };
      return levelLabels[currentUser.adminLevel] || 'Admin';
    }
    if (currentUser.role === 'moderator' && currentUser.position) {
      const positionLabels = {
        head: 'Head Moderator',
        senior: 'Senior Moderator',
        junior: 'Junior Moderator'
      };
      return positionLabels[currentUser.position] || 'Moderator';
    }
    return currentUser.role?.charAt(0).toUpperCase() + currentUser.role?.slice(1) || 'User';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getAdminLevelLabel = () => {
    const v = String(currentUser?.adminLevel || '').trim();
    const levelLabels = {
      super_admin: 'Super Admin',
      system_admin: 'System Admin',
      moderator: 'Moderator',
      content_manager: 'Content Manager',
      support_admin: 'Support Admin',
      finance_admin: 'Finance Admin',
      read_only_admin: 'Read Only Admin',
    };
    return levelLabels[v] || (v ? v : '—');
  };

  const handlePickAvatar = () => {
    try {
      avatarInputRef.current?.click?.();
    } catch (_) {}
  };

  const handleAvatarSelected = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setSelectedAvatarFile(file);
  };

  const handleSave = async () => {
    if (isSaving) return;
    try {
      setIsSaving(true);

      const formData = new FormData();
      if (profile.displayName !== undefined) formData.append('displayName', profile.displayName);
      if (profile.bio !== undefined) formData.append('bio', profile.bio);
      if (profile.city !== undefined) formData.append('city', profile.city);
      if (profile.country !== undefined) formData.append('country', profile.country);
      if (profile.email !== undefined) formData.append('email', profile.email);

      if (selectedAvatarFile) {
        formData.append('avatar', selectedAvatarFile);
      }

      const res = await userController.updateProfile(formData);
      if (res?.statusCode && Number(res.statusCode) >= 400) {
        showSnackbar(res?.message || 'Failed to update profile', 'error');
        return;
      }

      await auth.refreshUser();
      setSelectedAvatarFile(null);
      setIsEditing(false);
      showSnackbar('Profile updated', 'success');
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      showSnackbar(apiMessage || error?.message || 'Failed to update profile', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const openTwoFactorDialog = ({ enable }) => {
    setTwoFactorDialogOpen(true);
    setTwoFactorPendingEnable(Boolean(enable));
    setTwoFactorOtp('');
    setTwoFactorError('');
  };

  const handleToggleTwoFactor = async () => {
    if (!currentUser?.email) {
      showSnackbar('No email found for this account', 'error');
      return;
    }

    setTwoFactorError('');
    setTwoFactorSubmitting(true);
    try {
      const response = await authController.toggleTwoStepVerification(currentUser.email);
      const statusCode = response?.statusCode ?? response?.data?.statusCode;
      const ok =
        response?.success === true ||
        response?.data?.success === true ||
        (Number.isFinite(statusCode) && statusCode < 400);

      if (ok) {
        openTwoFactorDialog({ enable: !twoFactorEnabled });
        showSnackbar('Verification code sent to your email', 'success');
      } else {
        setTwoFactorError(response?.message || response?.data?.message || 'Failed to send verification code');
      }
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setTwoFactorError(apiMessage || err?.message || 'Failed to send verification code');
    } finally {
      setTwoFactorSubmitting(false);
    }
  };

  const handleVerifyTwoFactorOtp = async () => {
    if (!currentUser?.email) return;
    if (twoFactorOtp.length !== 6) {
      setTwoFactorError('Please enter a valid 6-digit verification code');
      return;
    }

    setTwoFactorSubmitting(true);
    setTwoFactorError('');
    try {
      const response = await authController.confirmTwoStepVerification(
        currentUser.email,
        twoFactorOtp,
        twoFactorPendingEnable,
      );

      const statusCode = response?.statusCode ?? response?.data?.statusCode;
      const ok =
        response?.success === true ||
        response?.data?.success === true ||
        (Number.isFinite(statusCode) && statusCode < 400);

      const enabledValue =
        response?.data?.enabled ??
        response?.data?.data?.enabled ??
        response?.enabled ??
        response?.data?.data?.data?.enabled;

      if (ok) {
        if (enabledValue !== undefined) {
          setTwoFactorEnabled(Boolean(enabledValue));
        }
        setTwoFactorDialogOpen(false);
        setTwoFactorOtp('');
        await auth.refreshUser();
        showSnackbar(
          Boolean(enabledValue)
            ? 'Two-factor authentication enabled'
            : 'Two-factor authentication disabled',
          'success',
        );
      } else {
        setTwoFactorError(response?.message || response?.data?.message || 'Invalid verification code');
      }
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setTwoFactorError(apiMessage || err?.message || 'Invalid verification code');
    } finally {
      setTwoFactorSubmitting(false);
    }
  };

  const handleResendTwoFactorOtp = async () => {
    if (!currentUser?.email) return;
    setTwoFactorOtp('');
    setTwoFactorError('');
    try {
      const response = await authController.toggleTwoStepVerification(currentUser.email);
      const statusCode = response?.statusCode ?? response?.data?.statusCode;
      const ok =
        response?.success === true ||
        response?.data?.success === true ||
        (Number.isFinite(statusCode) && statusCode < 400);

      if (ok) {
        showSnackbar('A new verification code has been sent to your email', 'success');
      } else {
        setTwoFactorError(response?.message || response?.data?.message || 'Failed to resend code');
      }
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setTwoFactorError(apiMessage || err?.message || 'Failed to resend code');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>
          Profile
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" mb={3}>
          Manage your profile and account settings
        </Typography>

        <Grid container spacing={3}>
          {/* Profile Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    alt={getDisplayName()}
                    src={getAvatar()}
                    sx={{ width: 150, height: 150, mb: 2, fontSize: '3rem' }}
                  >
                  </Avatar>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleAvatarSelected}
                  />
                  {isEditing && (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                      onClick={handlePickAvatar}
                    >
                      Change Avatar
                    </Button>
                  )}
                  <Typography variant="h6" fontWeight={600}>
                    {getDisplayName()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                    @{currentUser?.username}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Chip
                      label={getRoleLabel()}
                      color={getRoleColor(currentUser?.role)}
                      size="small"
                      icon={currentUser?.role === 'admin' ? <AdminPanelSettingsIcon /> : currentUser?.role === 'moderator' ? <ShieldIcon /> : undefined}
                    />
                    {currentUser?.isPremium && (
                      <Chip
                        label="Premium"
                        color="warning"
                        size="small"
                      />
                    )}
                    {currentUser?.isEmailVerified && (
                      <Chip
                        label="Verified"
                        color="success"
                        size="small"
                        icon={<VerifiedIcon />}
                      />
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    sx={{ mt: 2 }}
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    {isEditing ? 'Cancel' : 'Edit Profile'}
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={currentUser?.email || 'Not provided'}
                      primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
                    />
                  </ListItem>
                  {currentUser?.phoneNumber && (
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Phone"
                        secondary={currentUser.phoneNumber}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
                      />
                    </ListItem>
                  )}
                  {(currentUser?.profile?.city || currentUser?.profile?.country) && (
                    <ListItem>
                      <ListItemIcon>
                        <LocationOnIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Location"
                        secondary={`${currentUser.profile.city || ''}${currentUser.profile.city && currentUser.profile.country ? ', ' : ''}${currentUser.profile.country || ''}`}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
                      />
                    </ListItem>
                  )}
                  {currentUser?.role === 'admin' && currentUser?.adminLevel && (
                    <ListItem>
                      <ListItemIcon>
                        <WorkIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Admin Level"
                        secondary={getRoleLabel()}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
                      />
                    </ListItem>
                  )}
                  {currentUser?.role === 'moderator' && currentUser?.specialization && (
                    <ListItem>
                      <ListItemIcon>
                        <WorkIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Specialization"
                        secondary={currentUser.specialization}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
                      />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemIcon>
                      <CalendarTodayIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Joined"
                      secondary={formatDate(currentUser?.createdAt)}
                      primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} mb={3}>
                      Personal Information
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Display Name"
                          value={profile.displayName}
                          disabled={!isEditing}
                          onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Username"
                          value={currentUser?.username || ''}
                          disabled
                          size="small"
                          helperText="Username cannot be changed"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          value={profile.email}
                          disabled={!isEditing}
                          onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                          size="small"
                          helperText={isEditing ? 'Updating email may require verification' : 'Email'}
                        />
                      </Grid>
                      {currentUser?.role === 'admin' && (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Admin Level"
                            value={getAdminLevelLabel()}
                            disabled
                            size="small"
                            helperText="Admin level is managed by the system"
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Role"
                          value={getRoleLabel()}
                          disabled
                          size="small"
                          helperText="Role is assigned by administrators"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="City"
                          value={profile.city}
                          disabled={!isEditing}
                          onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Country"
                          value={profile.country}
                          disabled={!isEditing}
                          onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Bio"
                          value={profile.bio}
                          disabled={!isEditing}
                          onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                          multiline
                          rows={3}
                          size="small"
                          placeholder="Tell us about yourself..."
                        />
                      </Grid>
                    </Grid>

                    {isEditing && (
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button variant="outlined" onClick={() => setIsEditing(false)}>
                          Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                          {isSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Quick Actions */}
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} mb={2}>
                      Quick Actions
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>Two-Factor Authentication</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Require a verification code when signing in
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={twoFactorEnabled}
                                onChange={handleToggleTwoFactor}
                                disabled={twoFactorSubmitting || twoFactorDialogOpen}
                              />
                            }
                            label={twoFactorEnabled ? 'Enabled' : 'Disabled'}
                            labelPlacement="start"
                            sx={{ m: 0 }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<SecurityIcon />}
                          sx={{ justifyContent: 'flex-start', py: 1.5 }}
                        >
                          Change Password
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<NotificationsIcon />}
                          sx={{ justifyContent: 'flex-start', py: 1.5 }}
                        >
                          Notification Settings
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Account Statistics */}
              {(currentUser?.role === 'admin' || currentUser?.role === 'moderator') && (
                <Grid item xs={12}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} mb={2}>
                        Account Statistics
                      </Typography>
                      <Grid container spacing={2}>
                        {currentUser?.role === 'admin' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 2, color: 'error.dark' }}>
                                <Typography variant="h4" fontWeight={700}>
                                  {currentUser.totalActionsPerformed || 0}
                                </Typography>
                                <Typography variant="body2">
                                  Total Admin Actions
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2, color: 'info.dark' }}>
                                <Typography variant="h4" fontWeight={700}>
                                  {currentUser.lastAdminAction ? formatDate(currentUser.lastAdminAction) : 'Never'}
                                </Typography>
                                <Typography variant="body2">
                                  Last Admin Action
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                        {currentUser?.role === 'moderator' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'primary.dark' }}>
                                <Typography variant="h4" fontWeight={700}>
                                  {currentUser.totalModerationsPerformed || 0}
                                </Typography>
                                <Typography variant="body2">
                                  Total Moderations
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2, color: 'success.dark' }}>
                                <Typography variant="h4" fontWeight={700}>
                                  {currentUser.lastModeratorAction ? formatDate(currentUser.lastModeratorAction) : 'Never'}
                                </Typography>
                                <Typography variant="body2">
                                  Last Moderation
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Dialog open={twoFactorDialogOpen} onClose={() => setTwoFactorDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Verify Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the 6-digit verification code we sent to your email.
          </Typography>
          <TextField
            fullWidth
            label="Verification Code"
            value={twoFactorOtp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setTwoFactorOtp(value);
              setTwoFactorError('');
            }}
            placeholder="Enter 6-digit code"
            inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.3rem', letterSpacing: '0.35rem' } }}
            error={Boolean(twoFactorError)}
            helperText={twoFactorError || ' '}
            sx={{ mt: 1 }}
          />
          {twoFactorError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {twoFactorError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResendTwoFactorOtp} disabled={twoFactorSubmitting}>
            Resend
          </Button>
          <Button
            variant="contained"
            onClick={handleVerifyTwoFactorOtp}
            disabled={twoFactorSubmitting || twoFactorOtp.length !== 6}
          >
            {twoFactorSubmitting ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default Profile;
